import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { RefObject, useEffect } from "react";

/**
 * Locks background scroll while a modal is open.
 *
 * `overflow: hidden` on <html>/<body> (the `be-modal-open` lock) only hides the
 * scrollbar — it does NOT stop scroll *chaining*: wheel/touch deltas the modal's
 * own scroll area can't consume bubble up to the document and scroll it. When the
 * widget is embedded in a host page (or iframe), that surfaces as the page behind
 * the modal scrolling instead of the modal content. (SW-8621)
 *
 * This guard intercepts wheel/touch events inside the widget and only lets them
 * through when they land on a scrollable element inside the modal that can still
 * move in the requested direction; everything else is prevented, so the scroll can
 * never escape the modal.
 *
 * @param contentRef element wrapping the modal's scrollable content
 * @param isOpen     whether the modal is currently open
 */
export const useModalScrollLock = (contentRef: RefObject<HTMLElement | null>, isOpen: boolean) => {
  const { containerEl } = useShadowRoot();

  useEffect(() => {
    if (!isOpen) return;

    // Find the scrollable element the event lands on, but only inside the modal.
    // composedPath() is used so the real target is seen through the shadow DOM.
    const findScrollableInModal = (e: Event): HTMLElement | null => {
      const content = contentRef.current;
      if (!content) return null;
      const path = e.composedPath();
      const modalIndex = path.indexOf(content);
      if (modalIndex === -1) return null; // event originated outside the modal
      for (let i = 0; i < modalIndex; i++) {
        const el = path[i];
        if (!(el instanceof HTMLElement)) continue;
        const { overflowY } = getComputedStyle(el);
        const scrollable = overflowY === "auto" || overflowY === "scroll";
        if (scrollable && el.scrollHeight > el.clientHeight) return el;
      }
      return null;
    };

    const handleWheel = (e: WheelEvent) => {
      const el = findScrollableInModal(e);
      if (!el) {
        e.preventDefault();
        return;
      }
      const atTop = el.scrollTop <= 0;
      const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
      if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) e.preventDefault();
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!findScrollableInModal(e)) e.preventDefault();
    };

    // Capture phase so we run before any inner handler (e.g. the gallery carousel)
    // and before the browser's native scroll. Non-passive so preventDefault works.
    const opts: AddEventListenerOptions = { passive: false, capture: true };
    containerEl.addEventListener("wheel", handleWheel, opts);
    containerEl.addEventListener("touchmove", handleTouchMove, opts);
    return () => {
      containerEl.removeEventListener("wheel", handleWheel, opts);
      containerEl.removeEventListener("touchmove", handleTouchMove, opts);
    };
  }, [containerEl, contentRef, isOpen]);
};
